import React, { useEffect, useState } from 'react';
import {
  TicketFilters,
  TICKET_STATUS_LABELS,
  TICKET_PRIORITY_LABELS,
  TICKET_CHANNEL_LABELS,
} from '../../../types/ticketInterfaces';
import { helpdeskSettingsApi } from '../../../api/services/helpdesk-settings.service';
import { TicketType } from '../../../types/helpdeskSettingsInterfaces';

interface TicketsFiltersProps {
  filters: TicketFilters;
  setFilters: React.Dispatch<React.SetStateAction<TicketFilters>>;
  onApply: () => void;
  onReset: () => void;
  isFiltersApplied: boolean;
  pageSize: number;
  setPageSize: (size: number) => void;
}

export const TicketsFilters: React.FC<TicketsFiltersProps> = ({
  filters,
  setFilters,
  onApply,
  onReset,
  isFiltersApplied,
  pageSize,
  setPageSize,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [ticketTypes, setTicketTypes] = useState<TicketType[]>([]);

  useEffect(() => {
    helpdeskSettingsApi
      .getTicketTypes(true)
      .then((res) => setTicketTypes(res.data))
      .catch(() => setTicketTypes([]));
  }, []);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilters((prev) => ({
      ...prev,
      search: e.target.value,
    }));
  };

  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFilters((prev) => ({
      ...prev,
      status: e.target.value,
    }));
  };

  const handleAssignedChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFilters((prev) => ({
      ...prev,
      assigned_to: e.target.value,
    }));
  };

  const handleDateFromChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilters((prev) => ({
      ...prev,
      date_from: e.target.value,
    }));
  };

  const handleDateToChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilters((prev) => ({
      ...prev,
      date_to: e.target.value,
    }));
  };

  const handlePriorityChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFilters((prev) => ({
      ...prev,
      priority: e.target.value,
    }));
  };

  const handleChannelChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFilters((prev) => ({
      ...prev,
      channel: e.target.value,
    }));
  };

  const handleTicketTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFilters((prev) => ({
      ...prev,
      ticket_type: e.target.value,
    }));
  };

  const handleOrderingChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFilters((prev) => ({
      ...prev,
      ordering: e.target.value,
    }));
  };

  const handlePageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setPageSize(parseInt(e.target.value, 10));
  };

  return (
    <div className="w-full bg-white border border-gray-200 rounded-lg shadow-sm">
      {/* Header - Toggle Filter Panel */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center gap-2 text-gray-700 font-medium hover:text-gray-900 transition"
        >
          <span>⚙️ Настройка фильтрации</span>
          <span className={`transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}>
            ▼
          </span>
        </button>

        {isFiltersApplied && (
          <button
            onClick={onReset}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 border border-gray-300 rounded hover:bg-gray-50 transition"
          >
            Очистить
          </button>
        )}
      </div>

      {/* Filter Panel - Expandable/Collapsible */}
      {isExpanded && (
        <div className="px-6 py-6 space-y-6 border-b border-gray-200">
          {/* Search Input */}
          <div className="flex gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Поиск по номеру или теме
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={filters.search}
                  onChange={handleSearchChange}
                  placeholder="Введите номер тикета или тему..."
                  className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <span className="absolute left-3 top-2.5 text-gray-400">🔍</span>
              </div>
            </div>
          </div>

          {/* Filters Row */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                По статусу
              </label>
              <select
                value={filters.status}
                onChange={handleStatusChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              >
                <option value="">Все статусы</option>
                {Object.entries(TICKET_STATUS_LABELS).map(([key, label]) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
              </select>
            </div>

            {/* Assigned To Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                По ответственному
              </label>
              <select
                value={filters.assigned_to}
                onChange={handleAssignedChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              >
                <option value="">Все ответственные</option>
                {/* Populate with actual users - placeholder for now */}
              </select>
            </div>

            {/* Date From Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                От даты регистрации
              </label>
              <input
                type="date"
                value={filters.date_from}
                onChange={handleDateFromChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
            </div>

            {/* Priority Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                По приоритету
              </label>
              <select
                value={filters.priority}
                onChange={handlePriorityChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              >
                <option value="">Все приоритеты</option>
                {Object.entries(TICKET_PRIORITY_LABELS).map(([key, label]) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
              </select>
            </div>

            {/* Channel Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                По каналу
              </label>
              <select
                value={filters.channel}
                onChange={handleChannelChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              >
                <option value="">Все каналы</option>
                {Object.entries(TICKET_CHANNEL_LABELS).map(([key, label]) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
            {/* Ticket Type Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                По типу
              </label>
              <select
                value={filters.ticket_type}
                onChange={handleTicketTypeChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              >
                <option value="">Все типы</option>
                {ticketTypes.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Date To and Additional Filters */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {/* Date To Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                По дату регистрации
              </label>
              <input
                type="date"
                value={filters.date_to}
                onChange={handleDateToChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
            </div>
          </div>

          {/* Checkboxes and Action Buttons */}
          <div className="flex flex-wrap items-center gap-4 pt-2">
            {/* Checkboxes */}
            <div className="flex gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="w-4 h-4 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">Открытые</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="w-4 h-4 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">Без ответа</span>
              </label>
            </div>

            {/* Save Filter Button */}
            <button className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded transition">
              💾 Сохранить фильтр
            </button>

            {/* Add Filter Dropdown */}
            <div className="relative group">
              <button className="px-4 py-2 border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-medium rounded transition">
                + Добавить фильтр
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bottom Controls */}
      <div className="px-6 py-4 bg-gray-50 flex flex-wrap items-center justify-between gap-4">
        {/* Auto Refresh Toggle */}
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="w-4 h-4 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">Автообновление</span>
          </label>

          {autoRefresh && (
            <select className="px-3 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option>2 минуты</option>
              <option>5 минут</option>
              <option>10 минут</option>
              <option>30 минут</option>
            </select>
          )}
        </div>

        {/* Sort By Dropdown */}
        <div>
          <label className="text-sm text-gray-700 mr-2">Сортировать по:</label>
          <select
            value={filters.ordering}
            onChange={handleOrderingChange}
            className="px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="-created_at">Новые первыми</option>
            <option value="created_at">Старые первыми</option>
            <option value="-updated_at">Недавно обновленные</option>
            <option value="priority">По приоритету</option>
          </select>
        </div>

        {/* Show on Page Selector */}
        <div>
          <label className="text-sm text-gray-700 mr-2">Показать на странице:</label>
          <select
            value={pageSize}
            onChange={handlePageSizeChange}
            className="px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="25">25 строк</option>
            <option value="50">50 строк</option>
            <option value="100">100 строк</option>
            <option value="200">200 строк</option>
          </select>
        </div>

        {/* Apply Button */}
        <button
          onClick={onApply}
          className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded transition"
        >
          Применить
        </button>
      </div>
    </div>
  );
};
